import { Grid } from '@farewill/ui'
import { useFormikContext } from 'formik'

import Input from 'components/form/input'
import InputFloating from 'components/form/input-floating'
import { formatCurrency } from 'lib/formatting/currency'

import withForm from 'lib/formik/with-form'
import useValidatedSave from 'lib/formik/use-validated-save'
import { PERSONAL_POSSESSIONS_AND_VEHICLES_SCHEMA } from 'lib/probate/estate-questionnaire-schema'
import { ProbateEstate } from 'lib/models/probate-estate'

import Section from '../components/section'
import SectionTotals from '../components/section-totals'
import RadioGroup from '../components/radio-group'
import { FieldRow, FieldRowItem } from '../components/field-row'
import { YES_NO_DONT_KNOW_OPTIONS } from '../options'
import useSectionToggle from '../use-section-toggle'
import { RecordCollection } from './records-section'
import { sumValues, toNumber } from './value-total'
import Vehicles from './vehicles'

const PERSONAL_POSSESSIONS_SCHEMA =
  PERSONAL_POSSESSIONS_AND_VEHICLES_SCHEMA.omit(['vehicles'])

type HandleSave = (event: { name: string; value: unknown }) => void

type Props = {
  probateEstate: ProbateEstate
  saveProbateEstateField: HandleSave
  // every vehicle_or_possession item; only those with a vehicle type
  // are vehicles, the rest are possessions saved by the old fact-find
  vehicleItems: RecordCollection
}

// radio answers can't be cleared, so they don't lock the section
const CLEARABLE_FIELDS = [
  'householdPossessionsValue',
  'cashFoundAtHome',
  'personalPossessionsNotes',
  'safeDepositBoxNotes',
]

const PersonalPossessionsFields = ({
  probateEstate,
  saveProbateEstateField,
  vehicleItems,
}: Props) => {
  const { values } = useFormikContext<Record<string, unknown>>()

  const handleSave = useValidatedSave({
    schema: PERSONAL_POSSESSIONS_SCHEMA,
    save: saveProbateEstateField,
  })

  const toggle = useSectionToggle({
    name: 'vehiclesAndPossessionsSectionActive',
    initialValue: probateEstate.attributes.vehiclesAndPossessionsSectionActive,
    save: saveProbateEstateField,
  })

  const hasData = CLEARABLE_FIELDS.some(
    (field) =>
      values[field] !== null &&
      values[field] !== undefined &&
      values[field] !== ''
  )

  const vehicles = {
    ...vehicleItems,
    items: vehicleItems.items.filter((item) => item.attributes.vehicleType),
    // a vehicle's value is always an estimate unless it's bought new
    createItem: () =>
      vehicleItems.createItem({ vehicleType: 'car', isEstimatedValue: true }),
  }

  const possessionsAndCash =
    toNumber(values.householdPossessionsValue) +
    toNumber(values.cashFoundAtHome)
  const vehiclesTotal = sumValues(vehicles.items)

  return (
    <Section
      heading="2.2 Personal possessions and vehicles"
      notesName="personalPossessionsNotes"
      onNotesSave={handleSave}
      toggle={{ ...toggle, disabled: hasData || vehicles.items.length > 0 }}
      summary={
        <SectionTotals
          total={formatCurrency({ value: possessionsAndCash + vehiclesTotal })}
          breakdown={[
            {
              label: 'Possessions and cash',
              value: formatCurrency({ value: possessionsAndCash }),
            },
            {
              label: 'Vehicles',
              value: formatCurrency({ value: vehiclesTotal }),
            },
          ]}
        />
      }
      footer={<Vehicles collection={vehicles} />}
    >
      <Grid.Item span={12}>
        <FieldRow>
          <FieldRowItem>
            <InputFloating
              name="householdPossessionsValue"
              label="Total estimated value (£)"
              isCurrency
              handleSave={handleSave}
            />
          </FieldRowItem>
          <FieldRowItem>
            <InputFloating
              name="cashFoundAtHome"
              label="Cash found at home (£)"
              isCurrency
              handleSave={handleSave}
            />
          </FieldRowItem>
        </FieldRow>
      </Grid.Item>
      <Grid.Item span={12}>
        <RadioGroup
          label="Are the items insured?"
          name="householdItemsInsured"
          options={YES_NO_DONT_KNOW_OPTIONS}
          handleSave={handleSave}
          inline
        />
      </Grid.Item>
      <Grid.Item span={12}>
        <RadioGroup
          label="Did they have any property in a safe deposit box?"
          name="hasSafeDepositBox"
          options={YES_NO_DONT_KNOW_OPTIONS}
          handleSave={handleSave}
          inline
        />
      </Grid.Item>
      {/* kept when the answer changes, so a mis-click loses nothing */}
      {values.hasSafeDepositBox === 'yes' && (
        <Grid.Item span={12}>
          <Input
            name="safeDepositBoxNotes"
            label="Safe deposit box details"
            component="textarea"
            handleSave={handleSave}
          />
        </Grid.Item>
      )}
    </Section>
  )
}

const PersonalPossessionsForm = withForm(PersonalPossessionsFields)

const PersonalPossessions = ({
  probateEstate,
  saveProbateEstateField,
  vehicleItems,
}: Props) => {
  const { attributes } = probateEstate

  return (
    <PersonalPossessionsForm
      initialValues={{
        householdPossessionsValue: attributes.householdPossessionsValue ?? null,
        cashFoundAtHome: attributes.cashFoundAtHome ?? null,
        householdItemsInsured: attributes.householdItemsInsured ?? null,
        hasSafeDepositBox: attributes.hasSafeDepositBox ?? null,
        personalPossessionsNotes: attributes.personalPossessionsNotes ?? null,
        safeDepositBoxNotes: attributes.safeDepositBoxNotes ?? null,
      }}
      validationSchema={PERSONAL_POSSESSIONS_SCHEMA}
      probateEstate={probateEstate}
      saveProbateEstateField={saveProbateEstateField}
      vehicleItems={vehicleItems}
    />
  )
}

export default PersonalPossessions
